import { Heading } from "@solomon-apps/shared/src/components/ui/heading";
import { Text } from "@solomon-apps/shared/src/components/ui/text";
import { easings } from "@solomon-apps/shared/src/utils/easings";
import {
    Input,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    UploadFiles
} from "@solomon-labs/design/components/ui";
import { motion } from "motion/react";
import { type ReactElement, useMemo, useState } from "react";

import {
    type SignatoryAuthorityPayload,
    submitSignatoryAuthority
} from "@/services/clientOnboarding";
import { uploadOnboardingDocument } from "@/services/clientOnboardingDocuments";
import { resolveClientFormError } from "@/utils/clientFormErrors";

import { useOnboardingApplicationContextOrNull } from "../OnboardingApplicationContext";
import { useReportOnboardingDraft } from "../onboardingDraftContext";
import {
    type SignatorySource,
    useLegalEntityFormState,
    useSignatoryFormState,
    useSubmittedBeneficiaries
} from "../onboardingFormStateContext";
import { FormCard } from "../panel/FormCard";
import { FormPrimaryAction } from "../panel/FormPrimaryAction";
import { payloadIsDirty } from "../panel/payloadIsDirty";
import { DOCUMENT_ACCEPT, DOCUMENT_MAX_SIZE } from "../panel/uploadConstraints";
import type { NextStepTarget } from "../types";

/**
 * Structured form for §B.4 Authorized Signatory. Captures the signer's
 * legal name, email, and title plus an optional authorization document.
 *
 * In v1 there's no KYC provider wired yet, so the backend treats this
 * as a manual applicant-driven step that transitions straight to
 * Complete on submit (see yaas-api `OnboardingStepService.submit`'s
 * `isProviderBacked` change). When Persona / Sumsub / equivalent is
 * selected, this form gains a follow-up "Verify identity" CTA but the
 * field shape doesn't change.
 */
export function SignatoryAuthorityForm({
    nextStep,
    onContinue,
    slotIdx
}: {
    /** When non-null and the form is in a saved-clean state, the
     *  bottom button flips from "Saved" to "Continue to {title} →"
     *  and clicking it advances the hub to that subsection. */
    nextStep?: NextStepTarget | null;
    onContinue?: () => void;
    /** Focused sub-row from the rail. When set, only that sub-row's blocks
     *  render, so a rail row and its panel agree. Undefined (the section row
     *  itself) renders everything. Indices track `slots` order in
     *  `spec/onboardingSpec.ts`. */
    slotIdx?: number;
} = {}): ReactElement {
    // Sub-row 0 is "Signatory Details"; sub-row 1 is the authorization
    // document. Nothing renders only at section level.
    const showsIdentity = slotIdx === undefined || slotIdx === 0;
    const context = useOnboardingApplicationContextOrNull();

    // Field values + lastSubmitted snapshot live in the hub-scoped
    // form-state context so edits survive navigation. Server hydration is
    // handled once inside OnboardingFormStateProvider.
    const [state, setState] = useSignatoryFormState();
    const [legalEntity] = useLegalEntityFormState();
    const beneficiaries = useSubmittedBeneficiaries();
    const {
        signatorySource,
        signatoryName,
        signatoryEmail,
        signatoryTitle,
        docUuid,
        docFilename,
        lastSubmitted
    } = state;
    /**
     * The signer is usually someone the application has already named.
     * Offering them as sources means the same person is not typed twice
     * — and picking a beneficiary points at an entry whose identity
     * document is already on file.
     *
     * The legal contact is only offered when it is a distinct person:
     * `LegalContactSource` can itself mirror the signatory, and offering
     * both directions would let the two fields mirror each other.
     */
    const sourceOptions = useMemo<
        Array<{ value: SignatorySource | ""; label: string }>
    >(() => {
        // `""` is the null state — a real, always-present leading row
        // rather than a placeholder, so picking a person stays reversible.
        const opts: Array<{ value: SignatorySource | ""; label: string }> = [
            { value: "", label: "Different Than Existing Contacts" }
        ];
        if (legalEntity.primaryContactName.trim()) {
            opts.push({ value: "primary", label: "Same as Primary Contact" });
        }
        if (
            legalEntity.legalContactSource === null &&
            legalEntity.legalContactName.trim()
        ) {
            opts.push({ value: "legal", label: "Same as Legal Contact" });
        }
        for (const b of beneficiaries) {
            opts.push({
                value: `ubo:${b.entryUuid}`,
                label: `Same as ${b.legalName}`
            });
        }
        return opts;
    }, [
        legalEntity.primaryContactName,
        legalEntity.legalContactSource,
        legalEntity.legalContactName,
        beneficiaries
    ]);

    const mirrored = useMemo<{ name: string; email: string } | null>(() => {
        if (signatorySource === "primary") {
            return {
                name: legalEntity.primaryContactName,
                email: legalEntity.primaryContactEmail
            };
        }
        if (signatorySource === "legal") {
            return {
                name: legalEntity.legalContactName,
                email: legalEntity.legalContactEmail
            };
        }
        if (signatorySource?.startsWith("ubo:")) {
            const uuid = signatorySource.slice(4);
            const match = beneficiaries.find((b) => b.entryUuid === uuid);
            // A beneficiary carries no email — only the name mirrors, and
            // the email field stays open below.
            return match ? { name: match.legalName, email: "" } : null;
        }
        return null;
    }, [signatorySource, legalEntity, beneficiaries]);

    const mirrorsEmail = mirrored !== null && mirrored.email.trim().length > 0;
    const effectiveName = mirrored ? mirrored.name : signatoryName;
    const effectiveEmail = mirrorsEmail ? mirrored.email : signatoryEmail;

    const [uploading, setUploading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleUpload = async (files: FileList | null): Promise<void> => {
        if (!files || files.length === 0) return;
        const file = files[0];
        if (!context) {
            setState((prev) => ({
                ...prev,
                docUuid: `mock-${Date.now()}`,
                docFilename: file.name
            }));
            return;
        }
        setUploading(true);
        setError(null);
        try {
            const doc = await uploadOnboardingDocument({
                applicationUuid: context.application.uuid,
                purpose: "authorization_document",
                file
            });
            setState((prev) => ({
                ...prev,
                docUuid: doc.uuid,
                docFilename: doc.filename
            }));
        } catch (err) {
            setError(
                resolveClientFormError(
                    err,
                    "onboarding",
                    "Document upload failed. Choose the file again and retry."
                )
            );
        } finally {
            setUploading(false);
        }
    };

    const submittable =
        effectiveName.trim().length > 0 &&
        effectiveEmail.trim().length > 0 &&
        signatoryTitle.trim().length > 0;

    const currentPayload = useMemo<SignatoryAuthorityPayload | null>(() => {
        if (!submittable) return null;
        return {
            signatoryName: effectiveName.trim(),
            signatoryEmail: effectiveEmail.trim(),
            signatoryTitle: signatoryTitle.trim(),
            authorizationDocumentUuid: docUuid
        };
    }, [submittable, effectiveName, effectiveEmail, signatoryTitle, docUuid]);

    const isDirty =
        lastSubmitted !== null && payloadIsDirty(currentPayload, lastSubmitted);
    const hasPartialInput =
        signatoryName.trim().length > 0 ||
        signatoryEmail.trim().length > 0 ||
        signatoryTitle.trim().length > 0 ||
        docUuid !== null;
    const hasDraft = isDirty || (lastSubmitted === null && hasPartialInput);
    useReportOnboardingDraft("signatory-authority", hasDraft);

    const handleSave = async (): Promise<void> => {
        if (!currentPayload) return;
        if (!context) {
            setState((prev) => ({ ...prev, lastSubmitted: currentPayload }));
            return;
        }
        setSubmitting(true);
        setError(null);
        try {
            const result = await submitSignatoryAuthority(
                context.application.uuid,
                currentPayload
            );
            context.applyStepUpdate(result);
            setState((prev) => ({ ...prev, lastSubmitted: currentPayload }));
        } catch (err) {
            setError(
                resolveClientFormError(
                    err,
                    "onboarding",
                    "Authorized signatory details were not saved. Complete the required fields and try again."
                )
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, ease: easings.outCubic }}
        >
            <Heading
                title="Authorized Signatory"
                className="pb-4 border-b border-line-base"
            />

            {showsIdentity && (
                <FormCard title="Who Is Signing">
                    {/* Mirrors the §B.1 Legal Contact block: the
                        placeholder states the null case, so the
                        affirmative case gets said above the control. Not
                        a <label> — FormCard already titles the field and
                        the trigger carries its own aria-label. */}
                    <Text
                        variant="small"
                        className="block text-foreground-muted pb-2"
                    >
                        Same as Existing Contact?
                    </Text>
                    <Select
                        value={signatorySource ?? ""}
                        onValueChange={(next) => {
                            setState((prev) => ({
                                ...prev,
                                signatorySource:
                                    typeof next === "string" && next
                                        ? (next as SignatorySource)
                                        : null
                            }));
                        }}
                    >
                        <SelectTrigger
                            aria-label="Who is signing"
                            interaction="lightbar"
                            className="w-full min-w-0"
                        >
                            <SelectValue>
                                {
                                    sourceOptions.find(
                                        (o) =>
                                            o.value === (signatorySource ?? "")
                                    )?.label
                                }
                            </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                            {sourceOptions.map((opt) => (
                                <SelectItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </FormCard>
            )}

            {showsIdentity && (
                <FormCard title="Signatory's Legal Name">
                    <Input
                        aria-label="Signatory's Legal Name"
                        disabled={mirrored !== null}
                        value={effectiveName}
                        onChange={(e) =>
                            setState((prev) => ({
                                ...prev,
                                signatoryName: e.target.value
                            }))
                        }
                        placeholder="e.g. Sarah Chen"
                    />
                </FormCard>
            )}

            {showsIdentity && (
                <FormCard title="Signatory's Email">
                    <Input
                        aria-label="Signatory's Email"
                        type="email"
                        disabled={mirrorsEmail}
                        value={effectiveEmail}
                        onChange={(e) =>
                            setState((prev) => ({
                                ...prev,
                                signatoryEmail: e.target.value
                            }))
                        }
                        placeholder="e.g. sarah@crescent.com"
                    />
                </FormCard>
            )}

            {showsIdentity && (
                <FormCard title="Signatory's Title">
                    <Input
                        aria-label="Signatory's Title"
                        value={signatoryTitle}
                        onChange={(e) =>
                            setState((prev) => ({
                                ...prev,
                                signatoryTitle: e.target.value
                            }))
                        }
                        placeholder="e.g. Managing Director"
                    />
                </FormCard>
            )}

            {(slotIdx === undefined || slotIdx === 1) && (
                <FormCard
                    title="Authorization Document (Optional)"
                    hint="Documentation that proves the authorization for the Authorized Signatory."
                >
                    <UploadFiles
                        className="max-w-sm mx-auto mt-6"
                        size="sm"
                        accept={DOCUMENT_ACCEPT}
                        maxSize={DOCUMENT_MAX_SIZE}
                        state={
                            uploading
                                ? "uploading"
                                : docUuid
                                  ? "success"
                                  : "empty"
                        }
                        filename={docFilename ?? undefined}
                        onSelect={(files) => void handleUpload(files)}
                    />
                </FormCard>
            )}

            <FormPrimaryAction
                submittable={submittable}
                submitting={submitting}
                isSavedClean={lastSubmitted !== null && !isDirty}
                nextStep={nextStep ?? null}
                onContinue={onContinue}
                onSave={() => void handleSave()}
                error={error}
            />
        </motion.div>
    );
}
